import { motion } from 'framer-motion';
import { QuizQuestion } from '@shared/types';
import { MapPin, Check, Search } from 'lucide-react';
import { useState, useMemo, useEffect } from 'react';
import { useSearchCities } from '../../lib/api';
import { useT } from '../../lib/i18n';

interface CitySelectProps {
  question: QuizQuestion;
  value: string;
  onChange: (value: string) => void;
  onAutoAdvance?: (value: string) => void;
  /**
   * Optional cap on the number of cities returned. Default 8 (matches the
   * legacy scrolling-feed quiz). The paginated stepper's "Change my city"
   * full-list view passes a higher value (e.g. 500) to render every city
   * scrollably.
   */
  limit?: number;
  /**
   * When true, only show cities Outclose is currently serving (active in DB).
   * "Coming soon" entries are filtered out. Used by the paginated quiz so
   * users can't pick a city we don't yet support.
   */
  activeOnly?: boolean;
}

export default function CitySelect({ value, onChange, onAutoAdvance, limit, activeOnly }: CitySelectProps) {
  const { t } = useT();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce search query by 500ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: citiesResponse, isLoading } = useSearchCities(debouncedQuery, value, limit, activeOnly);
  
  // Use backend cities if available, otherwise fall back to question options
  const cities = useMemo(() => {
    if (citiesResponse?.success && citiesResponse.data) {
      return citiesResponse.data;
    }
    return [];
  }, [citiesResponse]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    if (onAutoAdvance) {
      onAutoAdvance(optionValue);
    }
  };
  const selectedCity = cities.find((option) => option.id === value);

  return (
    <div className="space-y-1">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-dark-muted" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('quiz.citySelect.searchPlaceholder')}
          className="w-full pl-12 pr-4 py-3 border-2 rounded-xl placeholder-dark-muted focus:outline-none focus:border-primary-500 transition-colors"
        />
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="text-center text-dark-muted py-4">
          {t('quiz.citySelect.loading')}
        </div>
      )}

      {/* No Results */}
      {!isLoading && cities.length === 0 && (
        <div className="text-center text-dark-muted py-8">
          <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>{t('quiz.citySelect.noResults', { query: searchQuery })}</p>
          <p className="text-sm mt-1">{t('quiz.citySelect.noResultsHint')}</p>
        </div>
      )}

      {/* Cities List */}
      {!isLoading && cities.map((option) => {
        const isSelected = value === option.id;
        const isAvailable = option.available;

        return (
          <motion.button
            key={option.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={() => handleSelect(option.id)}
            className={`
              w-full px-2 py-2 rounded-xl text-left transition-all
              cursor-pointer
              ${isSelected
                ? 'select-option-selected'
                : 'select-option'
              }
              ${!isAvailable ? 'opacity-70' : ''}
            `}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`
                  w-10 h-10 rounded-lg flex items-center justify-center
                  ${isSelected ? 'bg-primary-500/20' : 'bg-dark-border'}
                `}>
                  {option.emoji ? (
                    <span className="text-2xl">{option.emoji}</span>
                  ) : (
                    <MapPin className={`w-5 h-5 ${isSelected ? 'text-primary-500' : 'text-dark-muted'}`} />
                  )}
                </div>
                <div className="flex flex-col">
                  <span className="font-medium">{option.name}</span>
                  {!isAvailable && (
                    <span className="text-[11px] tracking-[0.12em] uppercase font-semibold text-primary-400/80 mt-0.5">
                      {t('quiz.citySelect.comingSoonBadge')}
                    </span>
                  )}
                </div>
              </div>
              {isSelected && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center"
                >
                  <Check className="w-4 h-4 text-white" />
                </motion.div>
              )}
            </div>
          </motion.button>
        );
      })}
      {selectedCity && !selectedCity.available && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 rounded-xl border border-primary-400/30 bg-primary-500/10 px-4 py-3 text-center"
        >
          <p className="text-[13px] font-semibold text-primary-300">
            {t('quiz.citySelect.comingSoonTitle', { city: selectedCity.name })}
          </p>
          <p className="text-[12px] text-neutral-400 mt-1">
            {t('quiz.citySelect.comingSoonDescription')}
          </p>
        </motion.div>
      )}
    </div>
  );
}

